import {VOICE_TRANSFORM, VOICE, CHORD_TYPE} from './ChordEffects.js';
import EffectEdge from "effects/rack/EffectEdge.js";
import MidiTrigger from "effects/midi/MidiTrigger.js";

window.voicingTimes = [];
window.flowTimes = [];
window.networkBuildTimes = [];

export default superclass => class extends superclass  {
    
    getAdjacentTransforms(voice) {
        let adj = this.adjacencyLists[voice.id] || [];

        return adj
            .map(e => e.effect)
            .filter(e => e.getChordEffectType() === VOICE_TRANSFORM);
    }

    getAdjacentVoices(chordNumeral) {
        let allChords = this.getChord(0);
        let chord = this.getChord(chordNumeral);

        let allAdj = this.adjacencyLists[allChords.id] || [];
        let chordAdj = this.adjacencyLists[chord.id] || [];

        return [... allAdj, ... chordAdj]
            .map(e => e.effect)
            .filter(e => e.getChordEffectType() === VOICE);
    }

    getChordElements(chordNumeral) {
        return this.getAdjacentVoices(chordNumeral)
            .map(e => e.degree);
    }

    getChordVoicing(chordNumeral) {
        // get which notes are to be played, as effects
        let voices = this.getAdjacentVoices(chordNumeral);

        // using these notes, determine if any are to be transformed
        let octaves = [];
        for (let voice of voices) {
            // see if theres any adjacenct
            let transforms = this.getAdjacentTransforms(voice);
            if (transforms.length === 0) {
                octaves.push(0);
            } else {
                octaves.push(transforms[0].value);
            }
        }
        return octaves;
    }

    getFlowChordVoicing(_chordNumeral) {
        // give this the standard scripting language form
        // so we can pass this function directly into the function
        // and have it lazily result in the voices it needs :)
        return (stepData, stepNumber, time) => {
            if (!stepData) {
                return [];
            }

            let dateA = new Date().getTime();
            let chordNumeral = _chordNumeral();

            // first prune the adjacency list of all nodes that are
            // of type CHORD_TYPE, starting at the given chord
            // i.e. give me the sub-graph that we care about for this chord
            let chord = this.getChord(chordNumeral);
            let subGraph, effects;
            if (chord.subGraph) {
                subGraph = chord.subGraph;
                effects = chord.effects;
            } else {

                subGraph = {};
                effects = [];
                for (let id in this.adjacencyLists) {
                    let effect = this.effects.find(x => x.id === id);
                    let outputId = id;
                    if (effect.getChordEffectType() === CHORD_TYPE &&
                        effect.numeral === 0) {
                        // this is an ALL CHORDS effect node, so pretend that
                        // its actually jjust another edge coming from the chord
                        outputId = chord.id;
                    } else if (effect.getChordEffectType() === CHORD_TYPE &&
                               effect !== chord) {
                        // we dont care about edges FROM other chords
                        continue;
                    }

                    // similarly, we don't care about edges TO other chord
                    let edges = this.adjacencyLists[id].filter(
                        ({effect}) => effect.getChordEffectType() !== CHORD_TYPE);
                    
                    // add the edges to this empty adjacency list
                    if (subGraph[outputId]) {
                        subGraph[outputId] = [... subGraph[outputId], ...edges];
                    } else {
                        subGraph[outputId] = edges;
                    }
                    effects = [... effects, effect, ... edges.map(x => x.effect)];
                }

                // any effects in this sub graph that have no out-bound edges
                // will be hooked up to a dummy OUTPUT_NODE, simulating that we
                // wish to collect thheir outputs
                
                for (let effect of effects) {
                    if (!subGraph[effect.id] || subGraph[effect.id].length === 0) {
                        subGraph[effect.id] = [
                            new EffectEdge(this.outputNode, this.track.sequencerEngine)
                        ];
                    }
                }
                chord.subGraph = subGraph;
                chord.effects = effects;
            }

            this.effects.forEach(s => s.active = false);
            // set root of the subgraph to this chord
            let root = chord;
            
            // run the adjacency list and root through the network
            // and look at the resulting MidiTrigger objects we get
            
            // these triggers will contain the voices we want

            // all voicing information will be stored in this list
            stepData.voices = [];

            let midiTrigger = new MidiTrigger(stepData, time, stepNumber);

            // first clear all the previous triggers from the graph, so we
            // can run from a clean-slate
            this.effects.forEach(e => {
                e.onNewStep();
                e.triggers.length=0;
            });

            window.networkBuildTimes.push(new Date().getTime() - dateA);

            chord.input(midiTrigger);
            let f1 = new Date().getTime();
            let resultingTriggers = this.runFlow(
                time,
                stepNumber,
                stepNumber,
                root,
                [midiTrigger],
                subGraph);
            
            let f2 = new Date().getTime();
            window.flowTimes.push(f2-f1);
                
            let voiceToOctave = {};
            for (let trigger of resultingTriggers) {
                let {time, stepData} = trigger;
                for (let {voice, octave, flat, thick} of stepData.voices) {
                    let key = voice+time;
                    if (flat) {
                        key += '+flat'+flat;
                    }
                    if (thick) {
                        key += '+thick'+thick;
                    }
                    if (!isNaN(octave)) {
                        key += '+'+octave;
                    }
                    voiceToOctave[key] = {octave, time, voice, flat, thick};
                }
            }
            let voices = [];
            for (let voiceTime in voiceToOctave) {
                let {time, octave, voice, flat, thick} = voiceToOctave[voiceTime];
                voices.push({voice, octave, time, flat, thick});
            }
            voices.sort((a,b) => a.voice - b.voice);
            let dateB = new Date().getTime();
            window.voicingTimes.push(dateB-dateA);
            return voices;
        };
    }

    getStep(step) {
        
        let stepToUse = this.cachedSteps[this.cacheIndex];
        this.cacheIndex = (this.cacheIndex + 1) % this.cachedSteps.length;

        stepToUse.voices = [... step.voices];
        return stepToUse;
    }
}
